import { ArrowLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface AIModel {
  id: string;
  name: string;
  company: string;
  aeo: number;
  aiReadiness: number;
}

interface UserScores {
  aeo: number;
  aiReadiness: number;
}

interface BenchmarkResultsProps {
  userScores: UserScores;
  selectedModels: AIModel[];
  onBack: () => void;
}

export function BenchmarkResults({ userScores, selectedModels, onBack }: BenchmarkResultsProps) {
  const getComparisonColor = (userScore: number, modelScore: number) => {
    const diff = userScore - modelScore;
    if (diff >= 5) return 'text-green-600';
    if (diff <= -5) return 'text-red-600';
    return 'text-yellow-600';
  };

  const getComparisonIcon = (userScore: number, modelScore: number) => {
    const diff = userScore - modelScore;
    if (diff >= 5) return <TrendingUp className="w-4 h-4" />;
    if (diff <= -5) return <TrendingDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getComparisonText = (userScore: number, modelScore: number) => {
    const diff = userScore - modelScore;
    if (diff >= 5) return `${Math.abs(diff)} points above`;
    if (diff <= -5) return `${Math.abs(diff)} points below`;
    return `Similar (±${Math.abs(diff)} points)`;
  };

  const getBarWidth = (score: number) => {
    return `${score}%`;
  };

  const getBarColor = (isUser: boolean, score: number) => {
    if (isUser) return 'bg-black';
    if (score >= 85) return 'bg-green-500';
    if (score >= 75) return 'bg-blue-500';
    return 'bg-gray-400';
  };

  const generateInsights = () => {
    const aeoComparisons = selectedModels.map(m => ({
      name: m.name,
      diff: userScores.aeo - m.aeo,
    }));

    const aiReadinessComparisons = selectedModels.map(m => ({
      name: m.name,
      diff: userScores.aiReadiness - m.aiReadiness,
    }));

    const insights = [];

    const aeoAbove = aeoComparisons.filter(c => c.diff >= 5);
    const aeoBelow = aeoComparisons.filter(c => c.diff <= -5);

    if (aeoAbove.length > 0) {
      insights.push(
        `Your AEO score exceeds ${aeoAbove.map(c => c.name).join(', ')} - excellent performance!`
      );
    }

    if (aeoBelow.length > 0) {
      insights.push(
        `Consider improving your AEO to match ${aeoBelow.map(c => c.name).join(', ')}`
      );
    }

    const aiAbove = aiReadinessComparisons.filter(c => c.diff >= 5);
    const aiBelow = aiReadinessComparisons.filter(c => c.diff <= -5);

    if (aiAbove.length > 0) {
      insights.push(
        `Your AI Readiness outperforms ${aiAbove.map(c => c.name).join(', ')}`
      );
    }

    if (aiBelow.length > 0) {
      insights.push(
        `Focus on AI Readiness optimization to compete with ${aiBelow.map(c => c.name).join(', ')}`
      );
    }

    if (insights.length === 0) {
      insights.push('Your scores are competitive with industry-leading AI models');
    }

    return insights;
  };

  const insights = generateInsights();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-700 hover:text-black mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Selection</span>
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Benchmark Comparison Results
          </h1>
          <p className="text-gray-600">
            Your scores compared against {selectedModels.length} industry-leading AI model
            {selectedModels.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* User Scores Summary */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Scores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">AEO Score</span>
                <span className="text-2xl font-bold text-gray-900">{userScores.aeo}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-black h-3 rounded-full transition-all"
                  style={{ width: getBarWidth(userScores.aeo) }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">AI Readiness Score</span>
                <span className="text-2xl font-bold text-gray-900">{userScores.aiReadiness}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-black h-3 rounded-full transition-all"
                  style={{ width: getBarWidth(userScores.aiReadiness) }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* AEO Comparison Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">AEO Score Comparison</h2>
          <div className="space-y-6">
            {/* User Score */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">Your Website</span>
                <span className="text-lg font-bold text-gray-900">{userScores.aeo}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div
                  className={`${getBarColor(true, userScores.aeo)} h-8 rounded-full transition-all flex items-center justify-end pr-3`}
                  style={{ width: getBarWidth(userScores.aeo) }}
                >
                  <span className="text-white text-xs font-medium">You</span>
                </div>
              </div>
            </div>

            {/* Model Scores */}
            {selectedModels.map((model) => (
              <div key={model.id}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{model.name}</span>
                    <span className="text-xs text-gray-500">({model.company})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">{model.aeo}</span>
                    <div
                      className={`flex items-center gap-1 ${getComparisonColor(
                        userScores.aeo,
                        model.aeo
                      )}`}
                    >
                      {getComparisonIcon(userScores.aeo, model.aeo)}
                      <span className="text-xs font-medium">
                        {getComparisonText(userScores.aeo, model.aeo)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div
                    className={`${getBarColor(false, model.aeo)} h-6 rounded-full transition-all`}
                    style={{ width: getBarWidth(model.aeo) }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Readiness Comparison Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-8 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            AI Readiness Score Comparison
          </h2>
          <div className="space-y-6">
            {/* User Score */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">Your Website</span>
                <span className="text-lg font-bold text-gray-900">{userScores.aiReadiness}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div
                  className={`${getBarColor(true, userScores.aiReadiness)} h-8 rounded-full transition-all flex items-center justify-end pr-3`}
                  style={{ width: getBarWidth(userScores.aiReadiness) }}
                >
                  <span className="text-white text-xs font-medium">You</span>
                </div>
              </div>
            </div>

            {/* Model Scores */}
            {selectedModels.map((model) => (
              <div key={model.id}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{model.name}</span>
                    <span className="text-xs text-gray-500">({model.company})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">
                      {model.aiReadiness}
                    </span>
                    <div
                      className={`flex items-center gap-1 ${getComparisonColor(
                        userScores.aiReadiness,
                        model.aiReadiness
                      )}`}
                    >
                      {getComparisonIcon(userScores.aiReadiness, model.aiReadiness)}
                      <span className="text-xs font-medium">
                        {getComparisonText(userScores.aiReadiness, model.aiReadiness)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div
                    className={`${getBarColor(false, model.aiReadiness)} h-6 rounded-full transition-all`}
                    style={{ width: getBarWidth(model.aiReadiness) }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Insights</h2>
          <ul className="space-y-3">
            {insights.map((insight, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 bg-black rounded-full mt-2 flex-shrink-0" />
                <p className="text-gray-700">{insight}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
